/**
 * Timezone utilities for consistent timezone handling across the application
 */

// Comprehensive list of common timezones with their display names and UTC offsets
export const TIMEZONE_OPTIONS = [
  // UTC and Greenwich Mean Time
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', offset: '+00:00', region: 'UTC' },
  { value: 'GMT', label: 'GMT (Greenwich Mean Time)', offset: '+00:00', region: 'Europe' },
  
  // North America - Eastern
  { value: 'America/New_York', label: 'EST/EDT (Eastern Time) - New York', offset: '-05:00/-04:00', region: 'North America' },
  { value: 'America/Toronto', label: 'EST/EDT (Eastern Time) - Toronto', offset: '-05:00/-04:00', region: 'North America' },
  { value: 'America/Montreal', label: 'EST/EDT (Eastern Time) - Montreal', offset: '-05:00/-04:00', region: 'North America' },
  
  // North America - Central
  { value: 'America/Chicago', label: 'CST/CDT (Central Time) - Chicago', offset: '-06:00/-05:00', region: 'North America' },
  { value: 'America/Mexico_City', label: 'CST (Central Time) - Mexico City', offset: '-06:00', region: 'North America' },
  { value: 'America/Winnipeg', label: 'CST/CDT (Central Time) - Winnipeg', offset: '-06:00/-05:00', region: 'North America' },
  
  // North America - Mountain
  { value: 'America/Denver', label: 'MST/MDT (Mountain Time) - Denver', offset: '-07:00/-06:00', region: 'North America' },
  { value: 'America/Phoenix', label: 'MST (Mountain Time) - Phoenix (no DST)', offset: '-07:00', region: 'North America' },
  { value: 'America/Calgary', label: 'MST/MDT (Mountain Time) - Calgary', offset: '-07:00/-06:00', region: 'North America' },
  
  // North America - Pacific
  { value: 'America/Los_Angeles', label: 'PST/PDT (Pacific Time) - Los Angeles', offset: '-08:00/-07:00', region: 'North America' },
  { value: 'America/Vancouver', label: 'PST/PDT (Pacific Time) - Vancouver', offset: '-08:00/-07:00', region: 'North America' },
  { value: 'America/Seattle', label: 'PST/PDT (Pacific Time) - Seattle', offset: '-08:00/-07:00', region: 'North America' },
  
  // North America - Alaska & Hawaii
  { value: 'America/Anchorage', label: 'AKST/AKDT (Alaska Time)', offset: '-09:00/-08:00', region: 'North America' },
  { value: 'Pacific/Honolulu', label: 'HST (Hawaii Time)', offset: '-10:00', region: 'North America' },
  
  // Europe - Western
  { value: 'Europe/London', label: 'GMT/BST (London)', offset: '+00:00/+01:00', region: 'Europe' },
  { value: 'Europe/Dublin', label: 'GMT/IST (Dublin)', offset: '+00:00/+01:00', region: 'Europe' },
  { value: 'Europe/Lisbon', label: 'WET/WEST (Lisbon)', offset: '+00:00/+01:00', region: 'Europe' },
  
  // Europe - Central
  { value: 'Europe/Paris', label: 'CET/CEST (Paris)', offset: '+01:00/+02:00', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'CET/CEST (Berlin)', offset: '+01:00/+02:00', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'CET/CEST (Madrid)', offset: '+01:00/+02:00', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'CET/CEST (Amsterdam)', offset: '+01:00/+02:00', region: 'Europe' },
  { value: 'Europe/Rome', label: 'CET/CEST (Rome)', offset: '+01:00/+02:00', region: 'Europe' },
  { value: 'Europe/Vienna', label: 'CET/CEST (Vienna)', offset: '+01:00/+02:00', region: 'Europe' },
  { value: 'Europe/Zurich', label: 'CET/CEST (Zurich)', offset: '+01:00/+02:00', region: 'Europe' },
  
  // Europe - Eastern
  { value: 'Europe/Helsinki', label: 'EET/EEST (Helsinki)', offset: '+02:00/+03:00', region: 'Europe' },
  { value: 'Europe/Athens', label: 'EET/EEST (Athens)', offset: '+02:00/+03:00', region: 'Europe' },
  { value: 'Europe/Kiev', label: 'EET/EEST (Kiev)', offset: '+02:00/+03:00', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'MSK (Moscow)', offset: '+03:00', region: 'Europe' },
  
  // Asia - Middle East
  { value: 'Asia/Dubai', label: 'GST (Dubai)', offset: '+04:00', region: 'Asia' },
  { value: 'Asia/Tehran', label: 'IRST (Tehran)', offset: '+03:30/+04:30', region: 'Asia' },
  { value: 'Asia/Jerusalem', label: 'IST (Jerusalem)', offset: '+02:00/+03:00', region: 'Asia' },
  
  // Asia - South Asia
  { value: 'Asia/Kolkata', label: 'IST (India Standard Time)', offset: '+05:30', region: 'Asia' },
  { value: 'Asia/Karachi', label: 'PKT (Pakistan Time)', offset: '+05:00', region: 'Asia' },
  { value: 'Asia/Dhaka', label: 'BST (Bangladesh Time)', offset: '+06:00', region: 'Asia' },
  
  // Asia - Southeast Asia
  { value: 'Asia/Bangkok', label: 'ICT (Bangkok)', offset: '+07:00', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'SGT (Singapore)', offset: '+08:00', region: 'Asia' },
  { value: 'Asia/Manila', label: 'PST (Manila)', offset: '+08:00', region: 'Asia' },
  { value: 'Asia/Jakarta', label: 'WIB (Jakarta)', offset: '+07:00', region: 'Asia' },
  { value: 'Asia/Kuala_Lumpur', label: 'MYT (Kuala Lumpur)', offset: '+08:00', region: 'Asia' },
  
  // Asia - East Asia
  { value: 'Asia/Shanghai', label: 'CST (China Standard Time)', offset: '+08:00', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'HKT (Hong Kong)', offset: '+08:00', region: 'Asia' },
  { value: 'Asia/Taipei', label: 'CST (Taiwan)', offset: '+08:00', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'JST (Japan Standard Time)', offset: '+09:00', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'KST (Korea Standard Time)', offset: '+09:00', region: 'Asia' },
  
  // Australia & Oceania
  { value: 'Australia/Sydney', label: 'AEST/AEDT (Sydney)', offset: '+10:00/+11:00', region: 'Australia' },
  { value: 'Australia/Melbourne', label: 'AEST/AEDT (Melbourne)', offset: '+10:00/+11:00', region: 'Australia' },
  { value: 'Australia/Brisbane', label: 'AEST (Brisbane)', offset: '+10:00', region: 'Australia' },
  { value: 'Australia/Perth', label: 'AWST (Perth)', offset: '+08:00', region: 'Australia' },
  { value: 'Australia/Adelaide', label: 'ACST/ACDT (Adelaide)', offset: '+09:30/+10:30', region: 'Australia' },
  { value: 'Pacific/Auckland', label: 'NZST/NZDT (Auckland)', offset: '+12:00/+13:00', region: 'Australia' },
  
  // South America
  { value: 'America/Sao_Paulo', label: 'BRT (São Paulo)', offset: '-03:00', region: 'South America' },
  { value: 'America/Buenos_Aires', label: 'ART (Buenos Aires)', offset: '-03:00', region: 'South America' },
  { value: 'America/Lima', label: 'PET (Lima)', offset: '-05:00', region: 'South America' },
  { value: 'America/Bogota', label: 'COT (Bogotá)', offset: '-05:00', region: 'South America' },
  
  // Africa
  { value: 'Africa/Cairo', label: 'EET (Cairo)', offset: '+02:00', region: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'SAST (Johannesburg)', offset: '+02:00', region: 'Africa' },
  { value: 'Africa/Lagos', label: 'WAT (Lagos)', offset: '+01:00', region: 'Africa' },
  { value: 'Africa/Nairobi', label: 'EAT (Nairobi)', offset: '+03:00', region: 'Africa' },
];

/**
 * Get the user's current timezone
 */
export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Unable to detect user timezone, falling back to UTC');
    return 'UTC';
  }
};

/**
 * Format timezone for display
 */
export const formatTimezone = (timezone) => {
  if (!timezone) return 'Not specified';
  
  const option = TIMEZONE_OPTIONS.find(tz => tz.value === timezone);
  if (option) {
    return option.label;
  }
  
  // Fallback for custom timezone strings
  return timezone;
};

/**
 * Get timezone offset for a given timezone
 */
export const getTimezoneOffset = (timezone) => {
  try {
    const now = new Date();
    const utc = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
    const targetTime = new Date(utc.toLocaleString('en-US', { timeZone: timezone }));
    const offset = (targetTime.getTime() - utc.getTime()) / (1000 * 60 * 60);
    
    const sign = offset >= 0 ? '+' : '-';
    const hours = Math.floor(Math.abs(offset));
    const minutes = Math.round((Math.abs(offset) - hours) * 60);
    
    return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch (error) {
    console.warn(`Unable to calculate offset for timezone: ${timezone}`);
    return '+00:00';
  }
};

/**
 * Convert time from one timezone to another
 */
export const convertTime = (date, fromTimezone, toTimezone) => {
  try {
    // If it's already a Date object, use it directly
    const sourceDate = date instanceof Date ? date : new Date(date);
    
    // Create a date string in the source timezone
    const sourceDateString = sourceDate.toLocaleString('en-US', { 
      timeZone: fromTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    // Parse this back to get the correct UTC time
    const parsedDate = new Date(sourceDateString);
    
    // Return the date in the target timezone
    return new Date(parsedDate.toLocaleString('en-US', { timeZone: toTimezone }));
  } catch (error) {
    console.warn(`Error converting time between timezones: ${error.message}`);
    return date;
  }
};

/**
 * Group timezones by region for organized display
 */
export const getGroupedTimezones = () => {
  const grouped = {};
  
  TIMEZONE_OPTIONS.forEach(timezone => {
    if (!grouped[timezone.region]) {
      grouped[timezone.region] = [];
    }
    grouped[timezone.region].push(timezone);
  });
  
  return grouped;
};

/**
 * Find timezone option by value
 */
export const findTimezoneOption = (value) => {
  return TIMEZONE_OPTIONS.find(tz => tz.value === value);
};

/**
 * Get popular/recommended timezones for quick access
 */
export const getPopularTimezones = () => {
  return [
    'America/New_York',     // EST/EDT
    'America/Chicago',      // CST/CDT  
    'America/Denver',       // MST/MDT
    'America/Los_Angeles',  // PST/PDT
    'Europe/London',        // GMT/BST
    'Europe/Paris',         // CET/CEST
    'Asia/Tokyo',           // JST
    'Asia/Shanghai',        // CST
    'Asia/Kolkata',         // IST
    'Australia/Sydney',     // AEST/AEDT
    'UTC'                   // UTC
  ].map(value => TIMEZONE_OPTIONS.find(tz => tz.value === value)).filter(Boolean);
};

/**
 * Validate if a timezone string is valid
 */
export const isValidTimezone = (timezone) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Get current time in a specific timezone
 */
export const getCurrentTimeInTimezone = (timezone) => {
  try {
    return new Date().toLocaleString('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.warn(`Unable to get current time for timezone: ${timezone}`);
    return new Date().toLocaleString();
  }
};